import {
    deleteStructureSet,
    getStructureSetDetail,
    patchStructureSetStructure,
    postStructureSetApproval,
    postStructureSetSkeleton,
    putStructureSetContent,
    putStructureSetReviews,
} from '~/backend/connector-next-api/structureSetSaveClient';
import type { StructureSetDetailResponse } from '~/backend/connector-next-api/connector-next-schemas';
import { PersistenceRequestError } from '~/domain/persistence/PersistenceErrors';
import type {
    ConnectorDatasetId,
    ConnectorPatientId,
    ConnectorVersionToken,
} from '~/domain/persistence/PersistenceRef';
import {
    asConnectorDatasetId,
    asConnectorVersionToken,
} from '~/domain/persistence/PersistenceRef';
import { toRecordedDatasetOrigin } from './toRecordedDatasetOrigin';
import { toRecordedRoiReviews } from './toRecordedRoiReviews';
import type {
    CreatedStructureSetDetail,
    ModelStructureNumbers,
    PersistedApprovalStatus,
    PersistedStructureSetDetail,
    StructureReviewNumbers,
    StructureSetPersistenceGateway,
} from '~/domain/persistence/StructureSetPersistenceGateway';

/**
 * Maps a wire detail DTO into the domain shape. A response without an id
 * or version is unusable for the save workflow's bookkeeping — treat it
 * as a failed request rather than proceeding with missing identity.
 */
function toDetail(
    dto: StructureSetDetailResponse
): PersistedStructureSetDetail {
    if (!dto.id || !dto.version) {
        throw new PersistenceRequestError(null, null);
    }
    return {
        datasetId: asConnectorDatasetId(dto.id),
        studyInstanceUid: dto.studyInstanceUid ?? null,
        seriesInstanceUid: dto.seriesInstanceUid ?? null,
        sopInstanceUid: dto.sopInstanceUid ?? null,
        approvalStatus: dto.approvalStatus ?? null,
        displayName: dto.displayName ?? null,
        structureCount: dto.structureCount ?? dto.structures?.length ?? 0,
        version: asConnectorVersionToken(dto.version),
        origin: toRecordedDatasetOrigin(dto.origin),
        roiReviews: toRecordedRoiReviews(dto.structures),
    };
}

/**
 * connector-next implementation of the persistence port.
 *
 * `currentUserName` is read at each write and sent as the client-supplied
 * user. The connector records it only where it cannot establish the caller
 * itself, and then marks it as unverified.
 */
export class ConnectorStructureSetGateway
    implements StructureSetPersistenceGateway
{
    constructor(
        private readonly currentUserName: () => string | undefined = () =>
            undefined
    ) {}

    async createSkeleton(request: {
        readonly patientId: ConnectorPatientId;
        readonly referencedImageSeriesUid: string;
        readonly displayName: string;
        readonly seriesInstanceUid?: string;
        readonly sopInstanceUid?: string;
        readonly provenanceTaskId?: string;
    }): Promise<CreatedStructureSetDetail> {
        const { detail, wasNew, afterInterruptedAttempt } =
            await postStructureSetSkeleton({
                patientId: request.patientId,
                referencedImageSeriesUid: request.referencedImageSeriesUid,
                displayName: request.displayName,
                seriesInstanceUid: request.seriesInstanceUid,
                sopInstanceUid: request.sopInstanceUid,
                provenanceTaskId: request.provenanceTaskId,
                clientUserName: this.currentUserName(),
            });
        return { ...toDetail(detail), wasNew, afterInterruptedAttempt };
    }

    async replaceContent(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        file: Blob,
        lockedRoiNumbers: readonly number[],
        modelStructures: ModelStructureNumbers,
        reviews: StructureReviewNumbers
    ): Promise<PersistedStructureSetDetail> {
        return toDetail(
            await putStructureSetContent(id, ifMatch, file, {
                lockedRoiNumbers,
                modelStructures,
                reviews,
                clientUserName: this.currentUserName(),
            })
        );
    }

    async setApproval(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        status: PersistedApprovalStatus,
        reviewerName: string | null
    ): Promise<PersistedStructureSetDetail> {
        return toDetail(
            await postStructureSetApproval(id, ifMatch, {
                status,
                reviewerName,
                clientUserName: this.currentUserName(),
            })
        );
    }

    async replaceStructureReviews(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        reviews: StructureReviewNumbers
    ): Promise<PersistedStructureSetDetail> {
        return toDetail(
            await putStructureSetReviews(id, ifMatch, {
                kept: [...reviews.kept],
                accepted: [...reviews.accepted],
                rejected: [...reviews.rejected],
                clientUserName: this.currentUserName(),
            })
        );
    }

    async updateStructureRow(
        id: ConnectorDatasetId,
        ifMatch: ConnectorVersionToken,
        roiNumber: number,
        fields: { readonly color?: string; readonly locked?: boolean }
    ): Promise<ConnectorVersionToken> {
        // The structure PATCH returns a StructureResponse body (no
        // id/version) — the set's new version comes from the ETag,
        // already extracted by the client.
        const result = await patchStructureSetStructure(
            id,
            ifMatch,
            roiNumber,
            fields
        );
        return asConnectorVersionToken(result.version);
    }

    async deleteStructureSet(id: ConnectorDatasetId): Promise<void> {
        await deleteStructureSet(id);
    }

    async getDetail(
        id: ConnectorDatasetId
    ): Promise<PersistedStructureSetDetail> {
        return toDetail(await getStructureSetDetail(id));
    }
}
